<template>
  <tbody class="sp-table-body" :class="classModifiers" part="body">
    <tr v-if="isEmpty" class="sp-table-body__empty-row">
      <td class="sp-table-body__empty" part="empty" :colspan="columnCount">
        <slot name="empty"></slot>
      </td>
    </tr>

    <template v-for="(item, index) in items" :key="rowKeyFor(item, index, rowKey)">
      <tr
        part="row"
        class="sp-table-body__row"
        :class="rowModifiers(index)"
        v-bind="attrsFor(item, index)"
        @click="onRowClick($event, item, index)"
      >
        <td v-if="isExpandable" part="toggle-cell" class="sp-table-body__toggle-cell">
          <TableRowToggle
            :expanded="isExpanded(item, index)"
            :controls="expansionId(index)"
            :label="toggleLabel(item)"
            @toggle="toggle(item, index)"
          />
        </td>
        <td
          v-for="column in columns"
          :key="column.field"
          part="cell"
          class="sp-table-body__cell"
          :class="cellClass(column)"
          :style="cellStyle(column)"
        >
          <TableCell :column="column" :item="item" />
        </td>
      </tr>

      <Transition
        enter-from-class="--collapsed"
        enter-active-class=""
        enter-to-class=""
        leave-from-class=""
        leave-active-class="--closing"
        leave-to-class="--collapsed"
        @before-leave="disableClosingExpansion"
      >
        <tr
          v-if="isExpanded(item, index)"
          part="expansion-row"
          class="sp-table-body__expansion-row"
          :class="stripeModifiers(index)"
        >
          <td :id="expansionId(index)" part="expansion" class="sp-table-body__expansion" :colspan="columnCount">
            <div class="sp-table-body__expansion-reveal">
              <div class="sp-table-body__expansion-content">
                <TableExpansion :expansion="expansion" :item="item" />
              </div>
            </div>
          </td>
        </tr>
      </Transition>
    </template>
  </tbody>
</template>

<script setup>
import { computed, useId } from "vue";

import { cellClass, cellStyle, rowKeyFor, TableCell, TableExpansion } from "#ce/components/SpTable/columns.js";
import { tAccessibleName } from "#ce/shared/components-i18n";
import TableRowToggle from "./TableRowToggle.ce.vue";

const INTERACTIVE_SELECTOR =
  "a[href], button, input, select, textarea, label, summary, [role=button], [role=link], " +
  // contenteditable is enumerated: the bare and empty forms enable editing, only "false" disables it.
  '[contenteditable]:not([contenteditable="false"])';

const props = defineProps({
  columns: {
    type: Array,
    default: () => [],
  },
  /**
   * The rows to render, already sorted and sliced.
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Field whose value identifies a row.
   */
  rowKey: {
    type: String,
    default: undefined,
  },
  /**
   * Attributes merged onto each row, as `(row, index) => Object`.
   */
  rowAttrs: {
    type: Function,
    default: undefined,
  },
  hover: {
    type: Boolean,
    default: false,
  },
  striped: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Content under an expanded row, as `(row) => VNode | string`. Rows are expandable while it is set.
   */
  expansion: {
    type: Function,
    default: undefined,
  },
  /**
   * Keys of the expanded rows, as `rowKeyFor` gives them.
   */
  expandedKeys: {
    type: Set,
    default: () => new Set(),
  },
});

const emit = defineEmits(["row-click", "toggle-expanded"]);

const idPrefix = useId();

const classModifiers = computed(() => ({
  "--hover": props.hover,
  "--striped": props.striped,
  "--loading": props.loading,
}));

const isEmpty = computed(() => props.items.length === 0);
const isExpandable = computed(() => typeof props.expansion === "function");
const columnCount = computed(() => props.columns.length + (isExpandable.value ? 1 : 0) || 1);

function attrsFor(item, index) {
  return props.rowAttrs ? props.rowAttrs(item, index) : undefined;
}

// Stripes follow the item's index, so an expanded row and its expansion share one.
function stripeModifiers(index) {
  return { "--alternate": index % 2 === 1 };
}

function rowModifiers(index) {
  const modifiers = stripeModifiers(index);

  if (isExpandable.value) {
    modifiers["--expandable"] = true;
  }

  return modifiers;
}

function isExpanded(item, index) {
  return isExpandable.value && props.expandedKeys.has(rowKeyFor(item, index, props.rowKey));
}

function expansionId(index) {
  return `${idPrefix}-expansion-${index}`;
}

// Returns the toggle's accessible name, taken from the row's first column so a screen reader user moving through
// the toggles hears which row each opens. A row whose first column holds no text gets a generic name.
function toggleLabel(item) {
  const value = item?.[props.columns[0]?.field];
  const label = typeof value === "string" || typeof value === "number" ? String(value).trim() : "";

  return label ? tAccessibleName("table_row_details", { label }) : tAccessibleName("table_row_details_unnamed");
}

function toggle(item, index) {
  emit("toggle-expanded", { item, index });
}

function onRowClick(event, item, index) {
  if (hasInteractiveTarget(event)) {
    return;
  }

  emit("row-click", { item, index });

  // A drag that selects text in the row is not a request to toggle it.
  if (isExpandable.value && !hasTextSelection()) {
    toggle(item, index);
  }
}

// Keeps keyboard focus out of a closing expansion, which stays in the page until its animation ends.
function disableClosingExpansion(row) {
  row.inert = true;
}

function hasTextSelection() {
  return window.getSelection()?.type === "Range";
}

// A control in a cell acts on its own row, so a click that reaches one is not a click on the row.
// The composed path crosses shadow boundaries, so a control an sp-* element renders is found here
// through the native element inside it.
function hasInteractiveTarget(event) {
  for (const node of event.composedPath()) {
    if (node === event.currentTarget) {
      return false;
    }

    if (node instanceof Element && node.matches(INTERACTIVE_SELECTOR)) {
      return true;
    }
  }

  return false;
}
</script>

<style lang="scss" scoped>
@use "../shared/cells";

.sp-table-body {
  &__cell {
    @include cells.cell-geometry;
    @include cells.cell-alignment;
  }

  &__toggle-cell {
    @include cells.toggle-cell;
  }

  &__expansion {
    padding: 0;
    border-bottom: var(--sp-comp-table-border-width, var(--sp-sys-border-width-default)) solid var(--table-border-color);
  }

  // Animating the track between 0fr and 1fr opens the expansion to its content's height, whatever that is. A grid
  // item's automatic minimum height is its content's, so the content sets its minimum to 0 and clips to let the
  // track close.
  &__expansion-reveal {
    display: grid;
    grid-template-rows: 1fr;
  }

  &__expansion-content {
    min-height: 0;
    overflow: hidden;
  }

  &__expansion-row.--collapsed {
    opacity: 0;
  }

  &__expansion-row.--collapsed &__expansion-reveal {
    grid-template-rows: 0fr;
  }

  &__expansion-row.--closing {
    pointer-events: none;
  }

  // `<Transition>` times the enter and leave from the row's own transition, so the reveal runs for the same duration.
  // Without a transition, as under reduced motion, the row mounts and unmounts at once.
  @media (prefers-reduced-motion: no-preference) {
    &__expansion-row {
      transition: opacity var(--sp-ref-transition-expand-duration) var(--sp-ref-transition-expand-easing);
    }

    &__expansion-reveal {
      transition: grid-template-rows var(--sp-ref-transition-expand-duration) var(--sp-ref-transition-expand-easing);
    }
  }

  &__empty {
    padding: var(--sp-comp-table-empty-padding, var(--sp-ref-scale-16));
    text-align: center;
    color: var(--table-header-color);
  }

  // A row runs on into its expansion, so the divider sits under the expansion instead, until the expansion is
  // removed at the end of its close.
  &__row:has(+ &__expansion-row) > td,
  > tr:last-child > td {
    border-bottom: none;
  }

  &.--striped &__row.--alternate,
  &.--striped &__expansion-row.--alternate {
    background-color: var(--sp-comp-table-striped-background-color, var(--sp-sys-color-surface-variant));
  }

  &__row.--expandable {
    cursor: pointer;
  }

  // Hover tints the row's cells, which paint over the row's own stripe, so a striped row darkens by the same step.
  &.--hover &__row {
    cursor: pointer;

    &:hover > td {
      background-color: var(
        --sp-comp-table-hover-background-color,
        color-mix(in srgb, var(--sp-sys-color-on-surface) 4%, transparent)
      );
    }
  }

  &.--loading {
    opacity: var(--sp-comp-table-loading-opacity, 0.5);
  }
}
</style>
